'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { buttonVariants } from '@/components/ui/Button';
import { ChevronDown, BookOpen, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';
import { cn } from '@/lib/utils';
import { FAQ_CATEGORIES } from './faq-categories';

export default function FAQContent() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>('gettingStarted');
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set());

  const { ref: heroRef, isVisible: heroVisible } = useIntersectionObserver();
  const { ref: faqRef, isVisible: faqVisible } = useIntersectionObserver();
  const { ref: ctaRef, isVisible: ctaVisible } = useIntersectionObserver();

  const toggleQuestion = (questionKey: string) => {
    setOpenQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionKey)) {
        next.delete(questionKey);
      } else {
        next.add(questionKey);
      }
      return next;
    });
  };

  return (
    <>
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container">
          <div ref={heroRef} className={`max-w-3xl mx-auto text-center fade-in-section ${heroVisible ? 'is-visible' : ''}`}>
            <span className="inline-block bg-primary-50 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t('pageContent.faq.hero.tagline')}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('pageContent.faq.hero.title')}
            </h1>
            <p className="text-xl text-gray-600 mb-12">
              {t('pageContent.faq.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div ref={faqRef} className={`fade-in-section ${faqVisible ? 'is-visible' : ''}`}>
            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {FAQ_CATEGORIES.map((category) => (
                <button
                  key={category.key}
                  onClick={() => setActiveCategory(category.key)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    activeCategory === category.key
                      ? 'bg-primary-500 text-white shadow-md'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {t(category.translationKey)}
                </button>
              ))}
            </div>

            {/*
              FAQ Accordion. EVERY category renders into the DOM; the inactive
              ones are hidden with CSS rather than left unrendered.

              This is a crawlability requirement, not a style choice. Rendering
              only `activeCategory` put 4 of the 16 questions in the server HTML
              while page.tsx emitted FAQPage JSON-LD for all 16 — structured data
              describing 12 answers no crawler could see. That is a Google
              structured-data policy violation (markup must reflect content
              visible on the page), so the exposure was a manual action, not just
              a lost rich result. Found by audit 2026-08-30, verified with:
                curl -sL https://www.sendvc.app/faq | grep -c '"@type":"Question"'
              Do not go back to rendering a single category.

              CSS-hidden is acceptable here for the same reason the collapsed
              accordion below is: the content is reachable by user interaction,
              which is the condition Google actually states.

              Each category carries its OWN space-y-3 wrapper. Moving space-y-3
              up to the outer container instead would hand the first visible card
              a stray margin-top whenever a preceding category is display:none.
            */}
            <div className="max-w-3xl mx-auto">
              {FAQ_CATEGORIES.map((category) => (
                <div
                  key={category.key}
                  className={cn('space-y-3', category.key !== activeCategory && 'hidden')}
                >
                  {category.questions.map((questionKey) => {
                    const isOpen = openQuestions.has(questionKey);
                    return (
                      <div
                        key={questionKey}
                        className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-shadow hover:shadow-md"
                      >
                        {/*
                          The heading WRAPS the toggle button (the WAI accordion pattern): the button
                          stays the only interactive control, while the h2 gives each question a real
                          document-outline anchor. Previously all 16 questions were plain <span>s, so
                          the page exposed just two headings and search/AI extractors had nothing to
                          anchor an answer to — the FAQPage JSON-LD alone does not create that outline.
                          h2 (not h3) because the category names above render as filter buttons, not
                          headings, so questions are the first content level under the h1.
                          globals.css applies var(--font-playfair) to h1-h6, so the body font is
                          restored on the heading to keep the accordion rendering pixel-identical.
                        */}
                        <h2 className="font-[family-name:var(--font-inter)]">
                          <button
                            onClick={() => toggleQuestion(questionKey)}
                            aria-expanded={isOpen}
                            aria-controls={`faq-answer-${questionKey}`}
                            className="w-full flex items-center justify-between p-6 text-left"
                          >
                            <span className="text-lg font-semibold text-gray-900 pr-4">
                              {t(`pageContent.faq.questions.${questionKey}.q`)}
                            </span>
                            <ChevronDown
                              className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${
                                isOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                        </h2>
                        <div
                          id={`faq-answer-${questionKey}`}
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                          }`}
                        >
                          <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                            {t(`pageContent.faq.questions.${questionKey}.a`)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="container">
          <div ref={ctaRef} className={`text-center max-w-2xl mx-auto fade-in-section ${ctaVisible ? 'is-visible' : ''}`}>
            <BookOpen className="h-12 w-12 mx-auto mb-6 text-primary-100" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('pageContent.faq.cta.title')}
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              {t('pageContent.faq.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {/*
                Styled anchors rather than <Link><Button> — a <button> nested inside an <a> is
                invalid HTML and gives crawlers a link with no readable target semantics.
                buttonVariants() reproduces the exact same classes the Button component emits.
              */}
              <Link
                href="/contact"
                className={cn(
                  buttonVariants({ variant: 'outline', size: 'lg' }),
                  'border-white text-white hover:bg-white/10 hover-scale'
                )}
              >
                {t('pageContent.faq.cta.contactButton')}
              </Link>
              <Link
                href="/#upload-section"
                className={cn(buttonVariants({ variant: 'secondary', size: 'lg' }), 'hover-scale')}
              >
                {t('pageContent.faq.cta.uploadButton')} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
